import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import {
  User, Mail, Lock, Save, Eye, EyeOff,
  CheckCircle, AlertCircle, Shield, Building2,
} from 'lucide-react';

const ROLE_LABELS: Record<string, { label: string; color: string }> = {
  superadmin: { label: 'Government / Platform Admin', color: 'text-purple-600 bg-purple-50 border-purple-200' },
  admin:      { label: 'Company Owner',                color: 'text-blue-600 bg-blue-50 border-blue-200' },
  operator:   { label: 'Operator',                     color: 'text-cyan-600 bg-cyan-50 border-cyan-200' },
  driver:     { label: 'Driver',                       color: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
};

function Toast({ msg, ok }: { msg: string; ok: boolean }) {
  return (
    <div className={`fixed bottom-6 right-6 z-[999] flex items-center gap-3 px-5 py-3 rounded-2xl shadow-modal text-white text-sm font-medium animate-slide-up`}
      style={{ background: ok ? 'linear-gradient(135deg,#059669,#047857)' : 'linear-gradient(135deg,#dc2626,#b91c1c)' }}>
      {ok ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
      {msg}
    </div>
  );
}

const Profile: React.FC = () => {
  const { user, role, companyId, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [fullName, setFullName] = useState(user?.name ?? '');
  const [phone, setPhone] = useState('');
  const [companyName, setCompanyName] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  // Password change
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [changingPw, setChangingPw] = useState(false);

  const showToast = (msg: string, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    async function load() {
      if (!user) return;
      // Load profile
      const { data: p } = await supabase.from('profiles').select('*').eq('id', user.id).single();
      if (p?.full_name) setFullName(p.full_name);

      // Load driver phone if driver role
      if (role === 'driver') {
        const { data: d } = await supabase.from('drivers').select('phone').eq('user_id', user.id).maybeSingle();
        if (d?.phone) setPhone(d.phone);
      }

      // Load company name
      if (companyId) {
        const { data: c } = await supabase.from('companies').select('name').eq('id', companyId).single();
        if (c) setCompanyName(c.name);
      }
    }
    load();
  }, [user, role, companyId]);

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    const { error } = await supabase.from('profiles').update({ full_name: fullName }).eq('id', user.id);
    if (error) {
      showToast(error.message, false);
    } else {
      await refreshProfile();
      showToast('Profile updated successfully');
    }
    setSaving(false);
  };

  const handlePasswordChange = async () => {
    if (newPw.length < 8) { showToast('Password must be at least 8 characters', false); return; }
    if (newPw !== confirmPw) { showToast('Passwords do not match', false); return; }
    setChangingPw(true);
    const { error } = await supabase.auth.updateUser({ password: newPw });
    if (error) { showToast(error.message, false); } else {
      showToast('Password updated successfully');
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
    }
    setChangingPw(false);
  };

  const roleMeta = ROLE_LABELS[role ?? 'operator'] ?? ROLE_LABELS['operator'];
  const initials = fullName.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?';

  return (
    <Layout title="My Profile" breadcrumbs={[{ label: 'Home', path: '/dashboard' }, { label: 'Profile' }]}>
      <div className="max-w-3xl mx-auto space-y-5">

        {/* ── Header card ── */}
        <div className="card p-6 flex flex-col sm:flex-row items-center sm:items-start gap-6">
          <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-electric-100 to-electric-50 flex items-center justify-center ring-4 ring-slate-100 shrink-0">
            <span className="text-4xl font-bold text-electric-500" style={{ fontFamily: 'Space Grotesk' }}>
              {initials}
            </span>
          </div>
          <div className="flex-1 text-center sm:text-left">
            <h2 className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Space Grotesk, Inter, sans-serif' }}>
              {fullName || '—'}
            </h2>
            <p className="text-slate-500 text-sm mt-1">{user?.email}</p>
            <div className="flex flex-wrap gap-2 mt-3 justify-center sm:justify-start">
              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${roleMeta.color}`}>
                <Shield className="w-3 h-3" />
                {roleMeta.label}
              </span>
              {companyName && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border border-slate-200 bg-slate-50 text-slate-600">
                  <Building2 className="w-3 h-3" />
                  {companyName}
                </span>
              )}
            </div>
          </div>
          {role === 'superadmin' && (
            <button onClick={() => navigate('/companies')}
              className="btn-primary text-sm px-4 py-2 shrink-0">
              Manage Companies
            </button>
          )}
        </div>

        {/* ── Edit profile ── */}
        <div className="card p-6">
          <h3 className="font-semibold text-slate-800 mb-5 flex items-center gap-2">
            <User className="w-4 h-4 text-electric-500" /> Personal Information
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1.5">Full Name</label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="input-premium"
                placeholder="Your full name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1.5">Email Address</label>
              <input type="email" value={user?.email ?? ''} disabled
                className="input-premium opacity-60 cursor-not-allowed bg-slate-50" />
            </div>
            {role === 'driver' && (
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1.5">Phone</label>
                <input type="text" value={phone} onChange={(e) => setPhone(e.target.value)}
                  className="input-premium" placeholder="+46 70 000 0000" />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1.5">Role</label>
              <input type="text" value={roleMeta.label} disabled
                className="input-premium opacity-60 cursor-not-allowed bg-slate-50" />
            </div>
          </div>
          <div className="mt-5 flex gap-3">
            <button onClick={handleSave} disabled={saving} className="btn-primary text-sm px-5 py-2 gap-2 disabled:opacity-60">
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>

        {/* ── Change password ── */}
        <div className="card p-6">
          <h3 className="font-semibold text-slate-800 mb-5 flex items-center gap-2">
            <Lock className="w-4 h-4 text-electric-500" /> Change Password
          </h3>
          <div className="space-y-4 max-w-sm">
            {[
              { label: 'Current Password',  value: currentPw, set: setCurrentPw },
              { label: 'New Password',      value: newPw,     set: setNewPw },
              { label: 'Confirm Password',  value: confirmPw, set: setConfirmPw },
            ].map((f) => (
              <div key={f.label}>
                <label className="block text-sm font-medium text-slate-600 mb-1.5">{f.label}</label>
                <div className="relative">
                  <input
                    type={showPw ? 'text' : 'password'}
                    value={f.value}
                    onChange={(e) => f.set(e.target.value)}
                    className="input-premium pr-10"
                    placeholder="••••••••"
                  />
                  <button type="button" onClick={() => setShowPw(!showPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            ))}
            <button onClick={handlePasswordChange} disabled={changingPw || !newPw}
              className="btn-primary text-sm px-5 py-2 gap-2 disabled:opacity-60">
              <Lock className="w-4 h-4" />
              {changingPw ? 'Updating...' : 'Update Password'}
            </button>
          </div>
          <p className="mt-3 text-xs text-slate-400 flex items-center gap-1">
            <Mail className="w-3 h-3" /> Password reset email can also be sent from {user?.email}
          </p>
        </div>

      </div>
      {toast && <Toast msg={toast.msg} ok={toast.ok} />}
    </Layout>
  );
};

export default Profile;
